import React from 'react';
import { X, Bot, Globe, Layers, Zap } from 'lucide-react';
import { TokenUsagePie } from './TokenUsagePie';

interface BotChatHeaderProps {
  /** Context type (stream or global) */
  context: 'stream' | 'global';
  /** Stream name for stream context */
  streamName?: string;
  /** Stream ID for stream context */
  streamId?: string;
  /** Total session tokens used */
  sessionTokens: number;
  /** Whether auto-approve is enabled */
  autoApprove: boolean;
  /** Callback to toggle auto-approve */
  onToggleAutoApprove: () => void;
  /** Callback when close is clicked */
  onClose: () => void;
}

/**
 * Header component for BotChat.
 * Displays context info, token usage, auto-approve toggle, and close button.
 */
export function BotChatHeader({
  context,
  streamName,
  streamId,
  sessionTokens,
  autoApprove,
  onToggleAutoApprove,
  onClose,
}: BotChatHeaderProps) {
  return (
    <div className="flex-shrink-0 border-b border-surface-600 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
            <Bot className="w-4 h-4 text-primary-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-text-primary text-sm">Assistant</h2>
              <div className="flex items-center gap-1 text-xs text-text-muted">
                {context === 'stream' ? (
                  <>
                    <Layers className="w-3 h-3" />
                    <span>{streamName || 'Current Stream'}</span>
                  </>
                ) : (
                  <>
                    <Globe className="w-3 h-3" />
                    <span>All Streams</span>
                  </>
                )}
              </div>
            </div>
            <TokenUsagePie sessionTokens={sessionTokens} size="sm" />
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* Auto-approve Toggle (only show if we have a stream) */}
          {streamId && (
            <button
              onClick={onToggleAutoApprove}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                autoApprove
                  ? 'bg-warning-500/20 text-warning-400'
                  : 'bg-surface-700 text-text-muted hover:text-text-primary'
              }`}
              title={autoApprove ? 'Auto-approve is ON' : 'Auto-approve is OFF'}
            >
              <Zap className="w-3 h-3" />
              {autoApprove ? 'Auto' : 'Manual'}
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 text-text-muted hover:text-text-primary hover:bg-surface-700 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
